import { createRoot, type Root } from 'react-dom/client'
import { $view } from '@milkdown/utils'
import type { NodeViewConstructor } from '@milkdown/prose/view'
import type { Node as ProseNode } from '@milkdown/prose/model'

import { MDAST_SNIPPET_BLOCK } from './snippetToken'
import {
  snippetBlockViewConfig,
  type SnippetReferenceSource,
} from './snippetReferenceSource'
import { remarkSnippetBlock, snippetBlockSchema } from './snippetRemark'
import { SnippetBlockView } from './SnippetBlockView'
import { snippetBodyHtml } from './snippetBodyHtml'

/**
 * A prompt holds the SHARED id even after this org customises the snippet, so
 * the org's copy is listed in the shared row's place and answers to its
 * `reference_id`. Its own id is accepted too, for a token that carries it.
 */
export function findSnippet(source: SnippetReferenceSource, id: string) {
  return findSnippetIn(source.getSnapshot().snippets, id)
}

const sameId = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

export function findSnippetIn<T extends { id: string; reference_id: string }>(
  snippets: readonly T[],
  id: string
): T | undefined {
  return (
    snippets.find((snippet) => sameId(snippet.reference_id, id)) ??
    snippets.find((snippet) => sameId(snippet.id, id))
  )
}

function isHiddenReference(source: SnippetReferenceSource, id: string) {
  return (
    source
      .getSnapshot()
      .hiddenReferenceIds?.some((hidden) => sameId(hidden, id)) ?? false
  )
}

const PLACEHOLDER_LABELS = {
  loading: 'Loading snippet',
  hidden: 'Snippet managed by Careforce',
  error: 'Snippet could not be loaded',
  unresolvable: 'Snippet not found',
}

export const snippetBlockNodeView = $view(
  snippetBlockSchema.node,
  (ctx): NodeViewConstructor =>
    (initialNode, view, getPos) => {
      const { snippetSource, interactive, dutyId, onEdit } = ctx.get(
        snippetBlockViewConfig.key
      )
      // The panel IS the NodeView element — one box, so ProseMirror's block
      // spacing and the visible padding cannot disagree.
      const dom = document.createElement('div')
      dom.setAttribute('data-snippet-block', '')
      dom.className = 'snippet-block my-3 rounded-xl bg-primary-ui-bg p-3!'
      let node = initialNode
      const root: Root = createRoot(dom)
      // Rendered here, not in the React tree: a root.render can still run after
      // the editor is destroyed, when ctx no longer has a parser.
      let cached = { body: '', html: '' }

      const remove = () => {
        if (!interactive || typeof getPos !== 'function') return
        const pos = getPos()
        if (pos === undefined) return
        view.dispatch(view.state.tr.delete(pos, pos + node.nodeSize))
        view.focus()
      }

      const render = () => {
        const id = String(node.attrs.id)
        const { status } = snippetSource.getSnapshot()
        const snippet = findSnippet(snippetSource, id)
        const hidden = !snippet && isHiddenReference(snippetSource, id)
        const customised = typeof snippet?.duty_content === 'string'
        const placeholder =
          status === 'loading'
            ? 'loading'
            : hidden
              ? 'hidden'
              : status === 'error'
                ? 'error'
                : 'unresolvable'
        dom.dataset.snippetId = id
        dom.dataset.snippetState =
          status === 'loading' || !snippet
            ? placeholder
            : customised
              ? 'customised'
              : 'resolved'
        dom.setAttribute(
          'aria-label',
          snippet
            ? `Snippet: ${snippet.name}${customised ? ', has a variant' : ''}`
            : PLACEHOLDER_LABELS[placeholder]
        )
        const body =
          (customised ? snippet?.duty_content : snippet?.content) ?? ''
        if (body !== cached.body) {
          cached = { body, html: body ? snippetBodyHtml(ctx, body) : '' }
        }
        root.render(
          <SnippetBlockView
            id={id}
            html={cached.html}
            status={status}
            snippet={snippet}
            hidden={hidden}
            interactive={interactive}
            dutyId={dutyId}
            onEdit={onEdit}
            onRemove={remove}
            onRetry={snippetSource.retry}
          />
        )
      }

      render()
      const unsubscribe = snippetSource.subscribe(render)

      return {
        dom,
        // Buttons inside the block are React-owned; ProseMirror must not treat
        // their clicks as editor input.
        stopEvent: (event) =>
          event.target instanceof Element &&
          Boolean(event.target.closest('button')),
        update: (updated: ProseNode) => {
          if (updated.type.name !== MDAST_SNIPPET_BLOCK) return false
          node = updated
          render()
          return true
        },
        destroy: () => {
          unsubscribe()
          // React refuses to unmount a root mid-render, and ProseMirror can
          // destroy a NodeView from inside a dispatch.
          queueMicrotask(() => root.unmount())
        },
      }
    }
)

export const snippetBlockPlugins = [
  snippetBlockViewConfig,
  remarkSnippetBlock,
  snippetBlockSchema,
  snippetBlockNodeView,
].flat()
